import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  IconButton,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  LocalOffer as DiscountIcon,
  Close as CloseIcon,
  AttachMoney as MoneyIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 320,
  },
  productInfo: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
  productName: {
    fontWeight: 600,
    fontSize: "0.95rem",
  },
  priceRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(0.5),
  },
  originalPrice: {
    color: theme.palette.text.secondary,
  },
  discountTypeSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  inputSection: {
    marginTop: theme.spacing(2),
  },
  previewSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.success.light + "20",
    borderRadius: 8,
    border: `1px solid ${theme.palette.success.light}`,
  },
  previewRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  finalPrice: {
    fontWeight: 700,
    color: theme.palette.success.dark,
    fontSize: "1.1rem",
  },
  discountValue: {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
  warningText: {
    color: theme.palette.warning.dark,
    fontSize: "0.75rem",
    marginTop: theme.spacing(1),
  },
}));

const DISCOUNT_TYPES = {
  PERCENTAGE: "percentage",
  MANUAL_PRICE: "manual_price",
};

const PdvDiscountModal = ({
  open,
  onClose,
  item,
  onApplyDiscount,
}) => {
  const classes = useStyles();
  const [discountType, setDiscountType] = useState(DISCOUNT_TYPES.PERCENTAGE);
  const [discountPercent, setDiscountPercent] = useState("");
  const [manualPrice, setManualPrice] = useState("");

  // Reset form when item changes
  useEffect(() => {
    if (item) {
      if (item.discountType === "manual_price" && item.manualPrice) {
        setDiscountType(DISCOUNT_TYPES.MANUAL_PRICE);
        setManualPrice(item.manualPrice.toString());
        setDiscountPercent("");
      } else if (item.discountPercent > 0) {
        setDiscountType(DISCOUNT_TYPES.PERCENTAGE);
        setDiscountPercent(item.discountPercent.toString());
        setManualPrice("");
      } else {
        setDiscountType(DISCOUNT_TYPES.PERCENTAGE);
        setDiscountPercent("");
        setManualPrice("");
      }
    }
  }, [item]);

  if (!item) return null;

  const originalPrice = item.originalPrice || item.unitPrice;
  const maxDiscountPercent = item.maxDiscountPercent || 100;
  const minSalePrice = item.minSalePrice || 0;
  const allowsDiscount = item.allowsDiscount !== false;

  // Calculate preview values
  const calculatePreview = () => {
    let finalPrice = originalPrice;
    let discountValue = 0;

    if (discountType === DISCOUNT_TYPES.PERCENTAGE) {
      const percent = parseFloat(discountPercent) || 0;
      if (percent > 0) {
        discountValue = originalPrice * (percent / 100);
        finalPrice = originalPrice - discountValue;
      }
    } else if (discountType === DISCOUNT_TYPES.MANUAL_PRICE) {
      const newPrice = parseFloat(manualPrice) || originalPrice;
      finalPrice = newPrice;
      discountValue = originalPrice - newPrice;
    }

    // Enforce minimum price
    if (finalPrice < minSalePrice) {
      finalPrice = minSalePrice;
      discountValue = originalPrice - minSalePrice;
    }

    return {
      finalPrice,
      discountValue,
      discountPercent: originalPrice > 0 ? (discountValue / originalPrice) * 100 : 0,
    };
  };

  const preview = calculatePreview();

  // Validate discount
  const validateDiscount = () => {
    if (!allowsDiscount) {
      toast.warning("Este produto não permite desconto");
      return false;
    }

    if (discountType === DISCOUNT_TYPES.PERCENTAGE) {
      const percent = parseFloat(discountPercent) || 0;
      if (percent < 0) {
        toast.warning("Desconto não pode ser negativo");
        return false;
      }
      if (percent > maxDiscountPercent) {
        toast.warning(`Desconto máximo permitido: ${maxDiscountPercent}%`);
        return false;
      }
    } else if (discountType === DISCOUNT_TYPES.MANUAL_PRICE) {
      const newPrice = parseFloat(manualPrice);
      if (isNaN(newPrice) || newPrice < 0) {
        toast.warning("Preço inválido");
        return false;
      }
      if (newPrice < minSalePrice) {
        toast.warning(`Preço mínimo permitido: R$ ${minSalePrice.toFixed(2)}`);
        return false;
      }
      if (newPrice > originalPrice) {
        toast.warning("Preço não pode ser maior que o original");
        return false;
      }
    }

    return true;
  };

  const handleApply = () => {
    if (!validateDiscount()) return;

    const discountData = {
      itemId: item.id,
      discountType,
      discountPercent: discountType === DISCOUNT_TYPES.PERCENTAGE
        ? parseFloat(discountPercent) || 0
        : preview.discountPercent,
      manualPrice: discountType === DISCOUNT_TYPES.MANUAL_PRICE
        ? parseFloat(manualPrice)
        : null,
      discountValue: preview.discountValue,
      finalPrice: preview.finalPrice,
    };

    onApplyDiscount(discountData);
    onClose();
  };

  const handleRemoveDiscount = () => {
    onApplyDiscount({
      itemId: item.id,
      discountType: "none",
      discountPercent: 0,
      manualPrice: null,
      discountValue: 0,
      finalPrice: originalPrice,
    });
    onClose();
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price || 0);
  };

  const hasDiscount = preview.discountValue > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <DiscountIcon color="primary" />
            <Typography variant="h6">Aplicar Desconto</Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {/* Product Info */}
        <div className={classes.productInfo}>
          <Typography className={classes.productName}>
            {item.name}
          </Typography>
          <div className={classes.priceRow}>
            <Typography variant="body2" className={classes.originalPrice}>
              Preço original:
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {formatPrice(originalPrice)}
            </Typography>
          </div>
          <div className={classes.priceRow}>
            <Typography variant="body2" className={classes.originalPrice}>
              Quantidade:
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {item.quantity}
            </Typography>
          </div>
        </div>

        {!allowsDiscount ? (
          <Typography color="error" align="center">
            Este produto não permite desconto
          </Typography>
        ) : (
          <>
            {/* Discount Type Selection */}
            <div className={classes.discountTypeSection}>
              <Typography variant="subtitle2" gutterBottom>
                Tipo de Desconto:
              </Typography>
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={discountType}
                  onChange={(e) => {
                    setDiscountType(e.target.value);
                    setDiscountPercent("");
                    setManualPrice("");
                  }}
                >
                  <FormControlLabel
                    value={DISCOUNT_TYPES.PERCENTAGE}
                    control={<Radio size="small" color="primary" />}
                    label="Desconto em porcentagem (%)"
                  />
                  <FormControlLabel
                    value={DISCOUNT_TYPES.MANUAL_PRICE}
                    control={<Radio size="small" color="primary" />}
                    label="Preço manual (R$)"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* Input Section */}
            <div className={classes.inputSection}>
              {discountType === DISCOUNT_TYPES.PERCENTAGE ? (
                <TextField
                  fullWidth
                  label="Desconto (%)"
                  type="number"
                  variant="outlined"
                  size="small"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    inputProps: { min: 0, max: maxDiscountPercent, step: 0.5 }
                  }}
                  helperText={`Máximo: ${maxDiscountPercent}%`}
                  autoFocus
                />
              ) : (
                <TextField
                  fullWidth
                  label="Novo Preço"
                  type="number"
                  variant="outlined"
                  size="small"
                  value={manualPrice}
                  onChange={(e) => setManualPrice(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    inputProps: { min: minSalePrice, max: originalPrice, step: 0.01 }
                  }}
                  helperText={minSalePrice > 0 ? `Mínimo: R$ ${minSalePrice.toFixed(2)}` : ""}
                  autoFocus
                />
              )}
            </div>

            {/* Preview Section */}
            {hasDiscount && (
              <div className={classes.previewSection}>
                <Typography variant="subtitle2" gutterBottom>
                  Prévia:
                </Typography>
                <div className={classes.previewRow}>
                  <Typography variant="body2">Desconto unitário:</Typography>
                  <Typography className={classes.discountValue}>
                    -{formatPrice(preview.discountValue)} ({preview.discountPercent.toFixed(1)}%)
                  </Typography>
                </div>
                <Divider style={{ margin: "8px 0" }} />
                <div className={classes.previewRow}>
                  <Typography variant="body2">Preço final unitário:</Typography>
                  <Typography className={classes.finalPrice}>
                    {formatPrice(preview.finalPrice)}
                  </Typography>
                </div>
                <div className={classes.previewRow}>
                  <Typography variant="body2">Total ({item.quantity}x):</Typography>
                  <Typography className={classes.finalPrice}>
                    {formatPrice(preview.finalPrice * item.quantity)}
                  </Typography>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {(item.discountPercent > 0 || item.manualPrice) && (
          <Button onClick={handleRemoveDiscount} color="secondary">
            Remover Desconto
          </Button>
        )}
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button
          onClick={handleApply}
          color="primary"
          variant="contained"
          disabled={!allowsDiscount || !hasDiscount}
          startIcon={<DiscountIcon />}
        >
          Aplicar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PdvDiscountModal;
